import React, { useState } from "react";
import { businessSignup } from "../ApiCalls/authentication";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { connect } from "react-redux";
import {
  userLogin,
  setUser,
  setToken,
  setUserId,
  setIsBusiness,
  setMantisBusiness,
} from "../Redux/modules/auth";
import { uploadImage } from "../ApiCalls/uploadFiles";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  Building2,
  FileText,
  UploadCloud,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  X,
  CheckCircle2,
  Check,
} from "lucide-react";

function mapDispatchToProps(dispatch) {
  return {
    userLogin: () => dispatch(userLogin()),
    setUser: (user) => dispatch(setUser(user)),
    setUserId: (userId) => dispatch(setUserId(userId)),
    setToken: (token) => dispatch(setToken(token)),
    setIsBusiness: (isBusiness) => dispatch(setIsBusiness(isBusiness)),
    setMantisBusiness: (mantisBusiness) =>
      dispatch(setMantisBusiness(mantisBusiness)),
  };
}

const select = (state) => {
  return {
    user: state.auth.user,
  };
};

function BusinessSignup(props) {
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [identificationType, setIdentificationType] = useState("NIT");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [contactPhone] = useState("");
  const [signingUp, setSigningUp] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  // Validations per step
  const isEmailValid = Boolean(
    email && email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
  );
  const isEmailMatch = Boolean(email && emailConfirm && email === emailConfirm);
  const isStep1Valid = Boolean(
    contactFirstName.trim() &&
    contactLastName.trim() &&
    isEmailValid &&
    isEmailMatch,
  );

  const isStep2Valid = Boolean(
    businessName.trim() && identificationNumber.trim(),
  );

  const isPasswordLengthValid = password.length >= 8;
  const isPasswordMatch = Boolean(
    password && passwordConfirm && password === passwordConfirm,
  );
  const isStep3Valid = Boolean(
    isPasswordLengthValid && isPasswordMatch && acceptedTerms,
  );

  function handleNextStep(e) {
    e.preventDefault();
    setErrorMessage("");
    if (currentStep === 1 && isStep1Valid) {
      setCurrentStep(2);
    } else if (currentStep === 2 && isStep2Valid) {
      setCurrentStep(3);
    }
  }

  function handlePrevStep() {
    setErrorMessage("");
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }

  async function submitSignup(event) {
    event.preventDefault();
    if (!isStep3Valid || signingUp) return;

    setErrorMessage("");
    setSigningUp(true);

    try {
      let logoUrl = "";
      if (logoFile) {
        if (logoFile.size > 20000000) {
          setErrorMessage("El tamaño máximo del logo es 20MB.");
          setSigningUp(false);
          return;
        }
        const formData = new FormData();
        formData.append("image", logoFile);

        const thumbnailJson = await uploadImage(formData);
        if (thumbnailJson && thumbnailJson.imageUrl) {
          logoUrl = thumbnailJson.imageUrl;
        } else {
          setErrorMessage("Error al subir el logo. Intenta de nuevo.");
          setSigningUp(false);
          return;
        }
      }

      const signupRes = await businessSignup(
        businessName,
        contactPhone,
        contactFirstName,
        contactLastName,
        "",
        [],
        [],
        "",
        logoUrl,
        identificationType,
        identificationNumber,
        email,
        password,
      );

      if (signupRes && signupRes.success) {
        await updateUserSession(
          signupRes.userId,
          signupRes.user,
          signupRes.token,
          signupRes.mantisBusiness,
        );
      } else {
        setErrorMessage(
          signupRes?.message ||
            "Error al registrar la cuenta. Verifica los datos.",
        );
      }
    } catch (err) {
      console.error("Signup error:", err);
      setErrorMessage("No se pudo conectar con el servidor. Intenta de nuevo.");
    } finally {
      setSigningUp(false);
    }
  }

  function updateUserSession(userId, user, token, mantisBusiness) {
    props.setUser({ user });
    props.setToken({ token });
    props.setUserId({ userId });
    props.setMantisBusiness({ mantisBusiness });
    props.userLogin();
    props.setIsBusiness({ isBusiness: true });
    navigate("/business/backoffice");
  }

  // B-86 — the sign-up in the same frame as the sign-in (`AuthLayout`); where the person is in the
  // three steps is said on the card ("Paso N de 3" and its bar). Only what is drawn changed —
  // the validations, the logo upload and `businessSignup` above are as they were.
  const campo =
    "h-10 w-full rounded-[8px] border bg-card pl-9 pr-3 text-[13px] text-brinks-foreground outline-none transition placeholder:text-brinks-muted-foreground/70 focus:border-brinks-border-strong focus:ring-2 focus:ring-brinks-ring/10 disabled:opacity-60";
  const campoError = "border-brinks-danger-border focus:border-brinks-danger";
  const etiqueta = "block text-[12px] font-medium text-brinks-muted-foreground";
  const icono =
    "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground";
  const botonPrincipal =
    "inline-flex h-10 w-full items-center justify-center gap-2 rounded-[8px] bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50";
  const botonSecundario =
    "inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-[8px] border bg-card px-4 text-[13px] font-medium text-brinks-foreground transition hover:bg-brinks-surface-hover disabled:opacity-50";
  const enlace =
    "font-medium text-brinks-foreground underline-offset-4 hover:underline";
  const ojo =
    "absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-[6px] text-brinks-muted-foreground transition hover:bg-brinks-surface-hover hover:text-brinks-foreground";

  const PASOS = [
    {
      n: 1,
      titulo: "Datos del responsable",
      texto: "Tu nombre y correo.",
    },
    {
      n: 2,
      titulo: "Datos de la empresa",
      texto: "Razón social, identificación y logotipo.",
    },
    {
      n: 3,
      titulo: "Seguridad y términos",
      texto: "Tu contraseña y la aceptación de los términos.",
    },
  ];
  const ENCABEZADO = {
    1: {
      titulo: "Datos del responsable",
      texto:
        "Ingresa tu nombre y el correo con el que administrarás la cuenta.",
    },
    2: {
      titulo: "Información de tu empresa",
      texto: "Datos legales y logotipo de la organización.",
    },
    3: {
      titulo: "Seguridad y términos",
      texto: "Crea tu clave de acceso y completa el registro.",
    },
  };

  const checklist = (ok, texto) => (
    <span
      className={`flex items-center gap-1.5 ${ok ? "text-brinks-success" : "text-brinks-muted-foreground"}`}
    >
      <span
        className={`grid h-4 w-4 place-items-center rounded-full ${ok ? "bg-brinks-success-soft" : "bg-brinks-surface"}`}
      >
        <Check className="h-3 w-3" />
      </span>
      {texto}
    </span>
  );

  return (
    <AuthLayout
      titulo="Crea tu cuenta en Mantis"
      subtitulo="Registra tu empresa en tres pasos para acceder a la plataforma con tu equipo."
    >
      <section className="rounded-[14px] border bg-card p-6 shadow-brinks-card sm:p-7">
        <div className="mb-5 grid gap-2">
          <div className="flex items-center justify-between text-[12px]">
            <span className="font-semibold text-brinks-foreground">
              Paso {currentStep} de 3
            </span>
            <span className="text-brinks-muted-foreground">
              {PASOS[currentStep - 1].titulo}
            </span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-brinks-surface">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            />
          </div>
        </div>

        <header className="mb-5">
          <h1 className="font-lora text-[22px] font-semibold text-brinks-foreground">
            {ENCABEZADO[currentStep].titulo}
          </h1>
          <p className="mt-1 text-[13px] text-brinks-muted-foreground">
            {ENCABEZADO[currentStep].texto}
          </p>
        </header>

        {errorMessage && (
          <div
            role="alert"
            className="mb-4 flex items-start gap-2 rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-[12.5px] text-brinks-danger"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{errorMessage}</p>
          </div>
        )}

        {currentStep === 1 && (
          <form onSubmit={handleNextStep} className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <label htmlFor="registro-nombre" className={etiqueta}>
                  Nombre(s) *
                </label>
                <div className="relative">
                  <User className={icono} />
                  <input
                    id="registro-nombre"
                    type="text"
                    value={contactFirstName}
                    onChange={(e) => setContactFirstName(e.target.value)}
                    placeholder="Juan Carlos"
                    className={campo}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="grid gap-1.5">
                <label htmlFor="registro-apellido" className={etiqueta}>
                  Apellido(s) *
                </label>
                <div className="relative">
                  <User className={icono} />
                  <input
                    id="registro-apellido"
                    type="text"
                    value={contactLastName}
                    onChange={(e) => setContactLastName(e.target.value)}
                    placeholder="Pérez García"
                    className={campo}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-1.5">
              <label htmlFor="registro-correo" className={etiqueta}>
                Correo electrónico *
              </label>
              <div className="relative">
                <Mail className={icono} />
                <input
                  id="registro-correo"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contacto@empresa.com"
                  className={campo}
                  required
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <label htmlFor="registro-correo-confirmar" className={etiqueta}>
                Confirmar correo electrónico *
              </label>
              <div className="relative">
                <Mail className={icono} />
                <input
                  id="registro-correo-confirmar"
                  type="email"
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                  placeholder="contacto@empresa.com"
                  className={`${campo} ${emailConfirm && email !== emailConfirm ? campoError : ""}`}
                  required
                />
              </div>
              {emailConfirm && email !== emailConfirm && (
                <p className="text-[12px] text-brinks-danger">
                  Los correos electrónicos no coinciden
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!isStep1Valid}
              className={`${botonPrincipal} mt-1`}
            >
              Continuar <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}

        {currentStep === 2 && (
          <form onSubmit={handleNextStep} className="grid gap-4">
            <div className="grid gap-1.5">
              <label htmlFor="registro-empresa" className={etiqueta}>
                Razón social / nombre de la empresa *
              </label>
              <div className="relative">
                <Building2 className={icono} />
                <input
                  id="registro-empresa"
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Ej. Inversiones Mantis S.A.S."
                  className={campo}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <label htmlFor="registro-identificacion" className={etiqueta}>
                Identificación tributaria *
              </label>
              <div className="flex gap-2">
                <select
                  aria-label="Tipo de identificación"
                  value={identificationType}
                  onChange={(e) => setIdentificationType(e.target.value)}
                  className="h-10 w-24 shrink-0 cursor-pointer rounded-[8px] border bg-card px-2 text-center text-[13px] font-medium text-brinks-foreground outline-none focus:border-brinks-border-strong"
                >
                  <option value="NIT">NIT</option>
                  <option value="CC">CC</option>
                  <option value="CE">CE</option>
                </select>
                <div className="relative flex-1">
                  <FileText className={icono} />
                  <input
                    id="registro-identificacion"
                    type="text"
                    value={identificationNumber}
                    onChange={(e) => setIdentificationNumber(e.target.value)}
                    placeholder="900123456-1"
                    className={campo}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-1.5">
              <span className={etiqueta}>
                Logotipo de la empresa (opcional)
              </span>
              <input
                type="file"
                id="companyLogoInput"
                accept="image/*"
                className="hidden"
                onChange={(e) => setLogoFile(e.target.files[0] || null)}
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() =>
                  document.getElementById("companyLogoInput").click()
                }
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  document.getElementById("companyLogoInput").click()
                }
                className="flex h-12 cursor-pointer items-center justify-between rounded-[8px] border border-dashed bg-brinks-surface px-3 text-[12.5px] text-brinks-muted-foreground transition hover:border-brinks-border-strong hover:bg-brinks-surface-hover"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <UploadCloud className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {logoFile
                      ? `Logo: ${logoFile.name}`
                      : "Cargar imagen de logo (PNG, JPG)"}
                  </span>
                </span>
                {logoFile ? (
                  <button
                    type="button"
                    aria-label="Quitar logo"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLogoFile(null);
                    }}
                    className="grid h-7 w-7 place-items-center rounded-[6px] hover:bg-card hover:text-brinks-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <span className="font-medium text-brinks-foreground">
                    Examinar
                  </span>
                )}
              </div>
            </div>

            <div className="mt-1 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handlePrevStep}
                className={botonSecundario}
              >
                <ArrowLeft className="h-4 w-4" /> Atrás
              </button>
              <button
                type="submit"
                disabled={!isStep2Valid}
                className={botonPrincipal}
              >
                Continuar <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        )}

        {currentStep === 3 && (
          <form onSubmit={submitSignup} className="grid gap-4">
            <div className="grid gap-1.5">
              <label htmlFor="registro-contrasena" className={etiqueta}>
                Contraseña *
              </label>
              <div className="relative">
                <Lock className={icono} />
                <input
                  id="registro-contrasena"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className={`${campo} pr-10`}
                  required
                  minLength={8}
                  disabled={signingUp}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={ojo}
                  disabled={signingUp}
                  aria-label="Alternar visibilidad"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="grid gap-1.5">
              <label
                htmlFor="registro-contrasena-confirmar"
                className={etiqueta}
              >
                Confirmar contraseña *
              </label>
              <div className="relative">
                <Lock className={icono} />
                <input
                  id="registro-contrasena-confirmar"
                  type={showPasswordConfirm ? "text" : "password"}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="Confirma tu contraseña"
                  className={`${campo} pr-10 ${passwordConfirm && password !== passwordConfirm ? campoError : ""}`}
                  required
                  minLength={8}
                  disabled={signingUp}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className={ojo}
                  disabled={signingUp}
                  aria-label="Alternar visibilidad"
                >
                  {showPasswordConfirm ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-[8px] border bg-brinks-surface px-3 py-2 text-[12px]">
              {checklist(isPasswordLengthValid, "Mínimo 8 caracteres")}
              {checklist(isPasswordMatch, "Las contraseñas coinciden")}
            </div>

            <label className="flex cursor-pointer items-start gap-2.5 text-[12.5px] leading-relaxed text-brinks-muted-foreground">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
                className="mt-0.5 h-4 w-4 cursor-pointer accent-[hsl(var(--primary))]"
                disabled={signingUp}
              />
              <span>
                Acepto las{" "}
                <a
                  href="/data-treatment-policies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={enlace}
                >
                  Políticas de Tratamiento de Datos
                </a>{" "}
                y los{" "}
                <a
                  href="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={enlace}
                >
                  Términos y Condiciones
                </a>{" "}
                de Mantis App S.A.S.
              </span>
            </label>

            <div className="mt-1 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handlePrevStep}
                disabled={signingUp}
                className={botonSecundario}
              >
                <ArrowLeft className="h-4 w-4" /> Atrás
              </button>
              <button
                type="submit"
                disabled={!isStep3Valid || signingUp}
                className={botonPrincipal}
              >
                {signingUp ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Creando…
                  </>
                ) : (
                  <>
                    Crear cuenta <CheckCircle2 className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </section>

      <p className="mt-5 text-center text-[12.5px] text-brinks-muted-foreground">
        ¿Ya tienes una cuenta?{" "}
        <button
          type="button"
          onClick={() => navigate("/")}
          className={enlace}
          disabled={signingUp}
        >
          Inicia sesión aquí
        </button>
      </p>
    </AuthLayout>
  );
}

export default connect(select, mapDispatchToProps)(BusinessSignup);
